import React, { useState, useRef } from 'react';
import { Users, Search, User } from 'lucide-react';
import C from '../../constants/colors';
import SiteCheckInModal from '../modals/SiteCheckInModal';
import { qrEncodeToCanvas } from '../../utils/qr';
import { matchesEmployeeSearch } from '../../utils/employeeSearch';
import * as hseApi from '../../api/hse';

export default function EmployeesView({ companies, sites, zones, employees, onAdd, onEdit, onRemove, onToggleActive, onCheckIn, onCheckOut, onEmployeeClick }) {
  // ── Access-level tab ──────────────────────────────────────────
  const [accessTab, setAccessTab] = useState('global');

  // ── Shared ───────────────────────────────────────────────────
  const [checkInTarget, setCheckInTarget] = useState(null);

  // ── Global tab state ─────────────────────────────────────────
  const [gSearch,   setGSearch]   = useState('');
  const [gCompany,  setGCompany]  = useState('');
  const [gSite,     setGSite]     = useState('');
  const [gRole,     setGRole]     = useState('');
  const [gStatus,   setGStatus]   = useState('ALL');

  // ── Company tab state ─────────────────────────────────────────
  const [cCompany,  setCCompany]  = useState(companies[0]?.id || '');
  const [cSite,     setCSite]     = useState('');
  const [cRole,     setCRole]     = useState('');
  const [cStatus,   setCStatus]   = useState('ALL');
  const [cSearch,   setCSearch]   = useState('');

  // ── Site tab state ────────────────────────────────────────────
  const [sCompany,  setSCompany]  = useState(companies[0]?.id || '');
  const [sSite,     setSSite]     = useState('');
  const [sRole,     setSRole]     = useState('');
  const [sStatus,   setSStatus]   = useState('ALL');
  const [sSearch,   setSSearch]   = useState('');

  // ── Bulk HSE selection ────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkModal, setBulkModal] = useState(null); // 'assign' | 'pass' | null

  function toggleSelect(id) {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }
  function clearSelection() { setSelectedIds([]); }

  // ── Badge printing ────────────────────────────────────────────
  async function printBadge(emp, company) {
    const qrCanvas = document.createElement('canvas');
    await qrEncodeToCanvas(window.location.origin + '/?e=' + emp.qrHash, qrCanvas, 160);
    const qrDataUrl = qrCanvas.toDataURL();
    const esc = s => String(s || '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
    const photoHtml = emp.photoUrl
      ? `<img src="${esc(emp.photoUrl)}" class="photo" />`
      : `<div class="photo-placeholder">${esc(emp.name.charAt(0).toUpperCase())}</div>`;
    const pinSvg = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="#009688"/><circle cx="12" cy="9" r="2.8" fill="white"/></svg>`;
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Badge – ${esc(emp.name)}</title><style>@page{size:85mm 54mm;margin:0}*{box-sizing:border-box;margin:0;padding:0}body{font-family:'Segoe UI',Arial,sans-serif;background:#e8e8e8;display:flex;justify-content:center;align-items:center;min-height:100vh}.badge{background:#fff;width:85mm;height:54mm;padding:3mm 4mm;overflow:hidden;box-shadow:0 4px 16px rgba(0,0,0,0.15);border-radius:2mm;display:flex;flex-direction:column;gap:2mm}.header{display:flex;align-items:center;gap:1.5mm}.co-name{font-size:10pt;font-weight:700;color:#111;line-height:1.2;border-bottom:0.7mm solid #009688;padding-bottom:0.6mm}.body{display:flex;justify-content:space-between;align-items:flex-start;gap:3mm;flex:1}.fields{flex:1;min-width:0;display:flex;flex-direction:column;justify-content:space-around}.field{margin-bottom:1.5mm}.field:last-child{margin-bottom:0}.fl{color:#999;font-size:5pt;font-weight:600;margin-bottom:0.4mm;text-transform:uppercase;letter-spacing:0.06em}.fv{color:#111;font-size:8.5pt;font-weight:700;line-height:1.2;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.right-col{display:flex;flex-direction:column;gap:1.5mm;flex-shrink:0}.photo{width:21mm;height:21mm;object-fit:cover;object-position:top;border-radius:1.5mm;display:block}.photo-placeholder{width:21mm;height:21mm;background:#e8e8e8;border-radius:1.5mm;display:flex;align-items:center;justify-content:center;font-size:16pt;font-weight:700;color:#bbb}.qr-wrap{background:#fff;padding:1mm;border:0.4mm solid #ddd;border-radius:1.5mm;display:inline-block}.qr{width:16mm;height:16mm;display:block}@media print{body{background:#fff;min-height:unset}.badge{box-shadow:none}}</style></head><body><div class="badge"><div class="header">${pinSvg}<span class="co-name">${esc(company?.name||'SafeCheck')}</span></div><div class="body"><div class="fields"><div class="field"><div class="fl">Name:</div><div class="fv">${esc(emp.name)}</div></div><div class="field"><div class="fl">ID Number:</div><div class="fv">${esc(emp.employeeId)}</div></div><div class="field"><div class="fl">Job Title:</div><div class="fv">${esc(emp.department||emp.role)}</div></div></div><div class="right-col">${photoHtml}<div class="qr-wrap"><img src="${qrDataUrl}" class="qr"/></div></div></div></div></body></html>`;
    const win = window.open('', '_blank');
    if (win) { win.document.write(html); win.document.close(); setTimeout(() => win.print(), 600); }
  }

  // ── Shared helpers ────────────────────────────────────────────
  const activeCount   = employees.filter(e => e.isActive !== false).length;
  const inactiveCount = employees.filter(e => e.isActive === false).length;
  const roleColors = { MANAGER: C.purple, STAFF: C.blue, CONTRACTOR: C.amber, VISITOR: C.textMuted };
  const selStyle = { background: C.surfaceElevated, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '7px 10px', color: C.textPrimary, fontSize: '12px', fontFamily: 'inherit', outline: 'none', cursor: 'pointer' };

  function applyBaseFilters(list, { search, role, status }) {
    return list.filter(e => {
      if (role   && e.role !== role)                          return false;
      if (status === 'ACTIVE'   && e.isActive === false)      return false;
      if (status === 'INACTIVE' && e.isActive !== false)      return false;
      if (!matchesEmployeeSearch(e, search)) return false;
      return true;
    });
  }

  // ── Row renderer (shared across all tabs) ────────────────────
  function renderRow(emp) {
    const company     = companies.find(c => c.id === emp.companyId);
    const site        = sites.find(s => s.id === emp.homeSiteId);
    const currentSite = emp.currentSiteId ? sites.find(s => s.id === emp.currentSiteId) : null;
    const isActive    = emp.isActive !== false;
    return (
      <tr key={emp.id} onClick={() => onEmployeeClick?.(emp.id)} style={{ borderTop: `1px solid ${C.border}`, background: isActive ? 'transparent' : 'rgba(0,0,0,0.15)', opacity: isActive ? 1 : 0.6, cursor: onEmployeeClick ? 'pointer' : 'default' }}>
        <td style={{ padding: '10px 8px' }} onClick={e => e.stopPropagation()}>
          <input type="checkbox" checked={selectedIds.includes(emp.id)} onChange={() => toggleSelect(emp.id)} />
        </td>
        <td style={{ padding: '12px 14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: isActive ? C.blue+'33' : C.border, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'visible', position: 'relative' }}
              onMouseEnter={e => { if (emp.photoUrl) { const r = e.currentTarget.getBoundingClientRect(); const t = document.getElementById(`photo-tip-${emp.id}`); if (t) { t.style.display = 'block'; t.style.left = (r.right + 8) + 'px'; t.style.top = (r.top + r.height/2 - 70) + 'px'; } } }}
              onMouseLeave={() => { const t = document.getElementById(`photo-tip-${emp.id}`); if (t) t.style.display = 'none'; }}>
              <div style={{ width: '32px', height: '32px', borderRadius: '50%', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {emp.photoUrl ? <img src={emp.photoUrl} alt={emp.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <User size={14} color={isActive ? C.blue : C.textMuted} />}
              </div>
              {emp.photoUrl && (
                <div id={`photo-tip-${emp.id}`} style={{ display: 'none', position: 'fixed', zIndex: 1000, pointerEvents: 'none' }}>
                  <img src={emp.photoUrl} alt={emp.name} style={{ width: '140px', height: '140px', objectFit: 'cover', borderRadius: '10px', boxShadow: '0 6px 24px rgba(0,0,0,0.6)', border: `2px solid ${C.border}` }} />
                </div>
              )}
            </div>
            <div>
              <div style={{ fontSize: '13px', fontWeight: 600, color: isActive ? C.textPrimary : C.textMuted }}>{emp.name}</div>
              <div style={{ fontSize: '11px', color: C.textMuted }}>{emp.department}</div>
              {emp.hseProfiler && (
                <span style={{ fontSize: '10px', fontWeight: 600, color: C.blue, background: C.blue + '22', padding: '1px 6px', borderRadius: '4px', display: 'inline-block', marginTop: '2px' }}>HSE Profiler</span>
              )}
            </div>
          </div>
        </td>
        <td style={{ padding: '12px 14px' }}>
          <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '11px', color: C.textSecondary }}>{emp.employeeId}</span>
        </td>
        <td style={{ padding: '12px 14px' }}>
          <div style={{ fontSize: '12px', color: C.textSecondary }}>{company?.name || '—'}</div>
          <div style={{ fontSize: '11px', color: C.textMuted }}>{site?.name || '—'}</div>
        </td>
        <td style={{ padding: '12px 14px' }}>
          <span style={{ fontSize: '11px', fontWeight: 600, color: roleColors[emp.role] || C.textMuted, background: (roleColors[emp.role] || C.textMuted)+'22', padding: '2px 8px', borderRadius: '6px' }}>{emp.role}</span>
        </td>
        <td style={{ padding: '12px 14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <div style={{ width: '7px', height: '7px', borderRadius: '50%', background: isActive ? C.green : C.textMuted, flexShrink: 0 }} />
            <span style={{ fontSize: '12px', color: isActive ? C.green : C.textMuted }}>{isActive ? 'Active' : 'Inactive'}</span>
          </div>
        </td>
        <td style={{ padding: '12px 14px' }}>
          {currentSite ? (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <div style={{ width: '7px', height: '7px', borderRadius: '50%', background: C.green, flexShrink: 0 }} />
                <span style={{ fontSize: '12px', color: C.green, fontWeight: 600 }}>On-site</span>
              </div>
              <div style={{ fontSize: '11px', color: C.textMuted, marginTop: '2px', paddingLeft: '12px' }}>{currentSite.name}</div>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <div style={{ width: '7px', height: '7px', borderRadius: '50%', background: C.textMuted, flexShrink: 0 }} />
              <span style={{ fontSize: '12px', color: C.textMuted }}>Off-site</span>
            </div>
          )}
        </td>
        <td style={{ padding: '12px 14px' }}>
          {emp.allowedOtherCompanies
            ? <span style={{ fontSize: '11px', color: C.purple, background: C.purple+'22', padding: '2px 8px', borderRadius: '6px' }}>Cross-company</span>
            : <span style={{ fontSize: '11px', color: C.textMuted }}>Own company</span>}
        </td>
        <td style={{ padding: '12px 14px' }} onClick={e => e.stopPropagation()}>
          <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            {currentSite ? (
              <button onClick={() => onCheckOut(emp.id)} style={{ padding: '5px 10px', borderRadius: '7px', border: `1px solid ${C.amber+'66'}`, background: C.amber+'11', color: C.amber, fontSize: '11px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>Check Out</button>
            ) : (
              isActive && (
                <button onClick={() => setCheckInTarget(emp)} style={{ padding: '5px 10px', borderRadius: '7px', border: `1px solid ${C.green+'66'}`, background: C.green+'11', color: C.green, fontSize: '11px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>Check In</button>
              )
            )}
            <button onClick={() => onToggleActive(emp.id)} style={{ padding: '5px 10px', borderRadius: '7px', border: `1px solid ${isActive ? C.border : C.green+'66'}`, background: 'transparent', color: isActive ? C.textMuted : C.green, fontSize: '11px', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>{isActive ? 'Deactivate' : 'Activate'}</button>
            <button onClick={() => printBadge(emp, company)} style={{ padding: '5px 10px', borderRadius: '7px', border: `1px solid ${'#009688'+'66'}`, background: '#00968811', color: '#009688', fontSize: '11px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>Badge</button>
            <button onClick={() => onEdit(emp)} style={{ padding: '5px 10px', borderRadius: '7px', border: `1px solid ${C.border}`, background: 'transparent', color: C.textSecondary, fontSize: '11px', cursor: 'pointer', fontFamily: 'inherit' }}>Edit</button>
            <button onClick={() => onRemove(emp.id)} style={{ padding: '5px 10px', borderRadius: '7px', border: `1px solid ${C.amber+'66'}`, background: C.amber+'11', color: C.amber, fontSize: '11px', cursor: 'pointer', fontFamily: 'inherit' }}>Archive</button>
          </div>
        </td>
      </tr>
    );
  }

  function renderTable(list, emptyMsg = 'No employees match the current filters') {
    return list.length === 0 ? (
      <div style={{ textAlign: 'center', color: C.textMuted, padding: '60px 0', fontSize: '14px' }}>{emptyMsg}</div>
    ) : (
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '12px', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: C.surfaceElevated }}>
              <th style={{ padding: '10px 8px', width: '36px' }}>
                <input
                  type="checkbox"
                  checked={list.length > 0 && list.every(e => selectedIds.includes(e.id))}
                  onChange={() => {
                    if (list.every(e => selectedIds.includes(e.id))) {
                      setSelectedIds(prev => prev.filter(id => !list.map(e => e.id).includes(id)));
                    } else {
                      setSelectedIds(prev => [...new Set([...prev, ...list.map(e => e.id)])]);
                    }
                  }}
                />
              </th>
              {['Employee','ID','Company / Site','Role','Status','Location','Access',''].map(h => (
                <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: '11px', fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>{list.map(emp => renderRow(emp))}</tbody>
        </table>
      </div>
    );
  }

  function StatusToggle({ value, onChange }) {
    return (
      <div style={{ display: 'flex', background: C.surfaceElevated, border: `1px solid ${C.border}`, borderRadius: '8px', overflow: 'hidden' }}>
        {[['ALL','All'],['ACTIVE','Active'],['INACTIVE','Inactive']].map(([k,l]) => (
          <button key={k} onClick={() => onChange(k)} style={{ padding: '7px 14px', border: 'none', background: value === k ? C.blue : 'transparent', color: value === k ? '#fff' : C.textMuted, fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>{l}</button>
        ))}
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>

      {/* ── Header ── */}
      <div style={{ padding: '20px 24px 0', borderBottom: `1px solid ${C.border}`, background: C.surface, flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
          <div>
            <h2 style={{ fontSize: '20px', fontWeight: 700, color: C.textPrimary, margin: 0 }}>Employees</h2>
            <div style={{ fontSize: '13px', color: C.textMuted, marginTop: '3px' }}>
              {employees.length} total &nbsp;·&nbsp;
              <span style={{ color: C.green }}>{activeCount} active</span>
              {inactiveCount > 0 && <span style={{ color: C.textMuted }}> &nbsp;·&nbsp; {inactiveCount} inactive</span>}
            </div>
          </div>
          <button onClick={onAdd} style={{ padding: '9px 18px', borderRadius: '9px', border: 'none', background: C.blue, color: '#fff', fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '7px' }}>
            <Users size={14} />Add Employee
          </button>
        </div>

        {/* Access-level tab strip */}
        <div style={{ display: 'flex', gap: '2px' }}>
          {[['global','Global'],['company','Company'],['site','Site']].map(([key, label]) => (
            <button key={key} onClick={() => setAccessTab(key)} style={{
              padding: '10px 20px', border: 'none', borderBottom: accessTab === key ? `2px solid ${C.blue}` : '2px solid transparent',
              background: 'transparent', color: accessTab === key ? C.blue : C.textMuted,
              fontSize: '13px', fontWeight: accessTab === key ? 600 : 400, cursor: 'pointer', fontFamily: 'inherit',
            }}>{label}</button>
          ))}
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════
          GLOBAL TAB — all employees, all existing filters
      ══════════════════════════════════════════════════════════ */}
      {accessTab === 'global' && (() => {
        const filteredSites = sites.filter(s => !gCompany || s.companyId === gCompany);
        const list = applyBaseFilters(
          employees.filter(e => {
            if (gCompany && e.companyId !== gCompany) return false;
            if (gSite    && e.homeSiteId !== gSite)   return false;
            return true;
          }),
          { search: gSearch, role: gRole, status: gStatus }
        );
        return (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ padding: '12px 24px', borderBottom: `1px solid ${C.border}`, background: C.surface, flexShrink: 0, display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
              <div style={{ position: 'relative', flex: '1', minWidth: '180px' }}>
                <Search size={13} color={C.textMuted} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
                <input value={gSearch} onChange={e => setGSearch(e.target.value)} placeholder="Search name, ID, department..."
                  style={{ width: '100%', background: C.surfaceElevated, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '7px 12px 7px 30px', color: C.textPrimary, fontSize: '12px', fontFamily: 'inherit', outline: 'none' }} />
              </div>
              <select value={gCompany} onChange={e => { setGCompany(e.target.value); setGSite(''); }} style={selStyle}>
                <option value=''>All Companies</option>
                {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <select value={gSite} onChange={e => setGSite(e.target.value)} style={selStyle}>
                <option value=''>All Sites</option>
                {filteredSites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              <select value={gRole} onChange={e => setGRole(e.target.value)} style={selStyle}>
                <option value=''>All Roles</option>
                {['MANAGER','STAFF','CONTRACTOR','VISITOR'].map(r => <option key={r} value={r}>{r}</option>)}
              </select>
              <StatusToggle value={gStatus} onChange={setGStatus} />
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>
              {renderTable(list)}
            </div>
          </div>
        );
      })()}

      {/* ══════════════════════════════════════════════════════════
          COMPANY TAB — scoped to one company
      ══════════════════════════════════════════════════════════ */}
      {accessTab === 'company' && (() => {
        const companySites = sites.filter(s => s.companyId === cCompany);
        const list = cCompany ? applyBaseFilters(
          employees.filter(e => {
            if (e.companyId !== cCompany) return false;
            if (cSite && e.homeSiteId !== cSite) return false;
            return true;
          }),
          { search: cSearch, role: cRole, status: cStatus }
        ) : [];
        const selectedCo = companies.find(c => c.id === cCompany);
        return (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Company selector bar */}
            <div style={{ padding: '12px 24px', borderBottom: `1px solid ${C.border}`, background: C.surface, flexShrink: 0, display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginRight: '4px' }}>
                <span style={{ fontSize: '12px', fontWeight: 600, color: C.textSecondary, whiteSpace: 'nowrap' }}>Company:</span>
                <select value={cCompany} onChange={e => { setCCompany(e.target.value); setCSite(''); }} style={{ ...selStyle, fontWeight: 600, color: C.textPrimary }}>
                  {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div style={{ width: '1px', height: '24px', background: C.border, flexShrink: 0 }} />
              <div style={{ position: 'relative', flex: '1', minWidth: '160px' }}>
                <Search size={13} color={C.textMuted} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
                <input value={cSearch} onChange={e => setCSearch(e.target.value)} placeholder="Search name, ID, department..."
                  style={{ width: '100%', background: C.surfaceElevated, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '7px 12px 7px 30px', color: C.textPrimary, fontSize: '12px', fontFamily: 'inherit', outline: 'none' }} />
              </div>
              <select value={cSite} onChange={e => setCSite(e.target.value)} style={selStyle}>
                <option value=''>All Sites</option>
                {companySites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              <select value={cRole} onChange={e => setCRole(e.target.value)} style={selStyle}>
                <option value=''>All Roles</option>
                {['MANAGER','STAFF','CONTRACTOR','VISITOR'].map(r => <option key={r} value={r}>{r}</option>)}
              </select>
              <StatusToggle value={cStatus} onChange={setCStatus} />
            </div>
            {/* Summary */}
            {selectedCo && (
              <div style={{ padding: '8px 24px', background: C.blue+'11', borderBottom: `1px solid ${C.blue}22`, flexShrink: 0, display: 'flex', gap: '16px', alignItems: 'center' }}>
                <span style={{ fontSize: '12px', fontWeight: 700, color: C.blue }}>{selectedCo.name}</span>
                <span style={{ fontSize: '12px', color: C.textMuted }}>
                  {employees.filter(e => e.companyId === cCompany && e.isActive !== false).length} active employees &nbsp;·&nbsp;
                  {companySites.length} sites
                </span>
              </div>
            )}
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>
              {!cCompany
                ? <div style={{ textAlign: 'center', color: C.textMuted, padding: '60px 0', fontSize: '14px' }}>Select a company to view employees</div>
                : renderTable(list)}
            </div>
          </div>
        );
      })()}

      {/* ══════════════════════════════════════════════════════════
          SITE TAB — scoped to one site
      ══════════════════════════════════════════════════════════ */}
      {accessTab === 'site' && (() => {
        const sCompanySites = sites.filter(s => s.companyId === sCompany);
        const list = sSite ? applyBaseFilters(
          employees.filter(e => e.homeSiteId === sSite),
          { search: sSearch, role: sRole, status: sStatus }
        ) : [];
        const selectedSiteName = sites.find(s => s.id === sSite)?.name;
        const selectedSiteCo   = companies.find(c => c.id === sCompany);
        return (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Company → Site selector bar */}
            <div style={{ padding: '12px 24px', borderBottom: `1px solid ${C.border}`, background: C.surface, flexShrink: 0, display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '12px', fontWeight: 600, color: C.textSecondary, whiteSpace: 'nowrap' }}>Company:</span>
                <select value={sCompany} onChange={e => { setSCompany(e.target.value); setSSite(''); }} style={{ ...selStyle, fontWeight: 600 }}>
                  {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '12px', fontWeight: 600, color: C.textSecondary, whiteSpace: 'nowrap' }}>Site:</span>
                <select value={sSite} onChange={e => setSSite(e.target.value)} style={{ ...selStyle, fontWeight: 600 }} disabled={!sCompany}>
                  <option value=''>Select site…</option>
                  {sCompanySites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div style={{ width: '1px', height: '24px', background: C.border, flexShrink: 0 }} />
              <div style={{ position: 'relative', flex: '1', minWidth: '160px' }}>
                <Search size={13} color={C.textMuted} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
                <input value={sSearch} onChange={e => setSSearch(e.target.value)} placeholder="Search name, ID, department..."
                  style={{ width: '100%', background: C.surfaceElevated, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '7px 12px 7px 30px', color: C.textPrimary, fontSize: '12px', fontFamily: 'inherit', outline: 'none' }} />
              </div>
              <select value={sRole} onChange={e => setSRole(e.target.value)} style={selStyle}>
                <option value=''>All Roles</option>
                {['MANAGER','STAFF','CONTRACTOR','VISITOR'].map(r => <option key={r} value={r}>{r}</option>)}
              </select>
              <StatusToggle value={sStatus} onChange={setSStatus} />
            </div>
            {/* Summary */}
            {sSite && (
              <div style={{ padding: '8px 24px', background: C.blue+'11', borderBottom: `1px solid ${C.blue}22`, flexShrink: 0, display: 'flex', gap: '16px', alignItems: 'center' }}>
                <span style={{ fontSize: '12px', fontWeight: 700, color: C.blue }}>{selectedSiteName}</span>
                <span style={{ fontSize: '12px', color: C.textMuted }}>
                  {selectedSiteCo?.name} &nbsp;·&nbsp;
                  {employees.filter(e => e.homeSiteId === sSite && e.isActive !== false).length} registered employees
                </span>
              </div>
            )}
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>
              {!sSite
                ? <div style={{ textAlign: 'center', color: C.textMuted, padding: '60px 0', fontSize: '14px' }}>Select a company and site to view employees</div>
                : renderTable(list)}
            </div>
          </div>
        );
      })()}

      {/* ── Check-in modal ── */}
      {checkInTarget && (
        <SiteCheckInModal
          employee={checkInTarget}
          companies={companies}
          sites={sites}
          zones={zones}
          onCheckIn={data => onCheckIn(checkInTarget.id, data)}
          onClose={() => setCheckInTarget(null)}
        />
      )}

      {/* ── Bulk HSE footer ── */}
      {selectedIds.length > 0 && (
        <div style={{
          position: 'sticky', bottom: 0, background: C.surfaceElevated, border: `1px solid ${C.border}`,
          borderRadius: '8px', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: '12px',
          margin: '0 0 12px', boxShadow: '0 -4px 16px rgba(0,0,0,0.3)',
        }}>
          <span style={{ color: C.textPrimary, fontSize: '13px', fontWeight: 600 }}>{selectedIds.length} selected</span>
          <button onClick={clearSelection} style={{ background: 'none', border: `1px solid ${C.border}`, color: C.textMuted, borderRadius: '5px', padding: '4px 10px', fontSize: '12px', cursor: 'pointer' }}>Clear</button>
          <button
            onClick={() => setBulkModal('assign')}
            style={{ background: C.blue + '22', color: C.blue, border: `1px solid ${C.blue}44`, borderRadius: '5px', padding: '4px 10px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
          >
            Assign Training
          </button>
          <button
            onClick={() => setBulkModal('pass')}
            style={{ background: C.green + '22', color: C.green, border: `1px solid ${C.green}44`, borderRadius: '5px', padding: '4px 10px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
          >
            Mark Group Pass
          </button>
        </div>
      )}

      {bulkModal && (
        <BulkTrainingModal
          mode={bulkModal}
          employeeIds={selectedIds}
          companyId={companies[0]?.id}
          onDone={() => { setBulkModal(null); clearSelection(); }}
          onClose={() => setBulkModal(null)}
        />
      )}
    </div>
  );
}

function BulkTrainingModal({ mode, employeeIds, companyId, onDone, onClose }) {
  const [courses, setCourses] = useState([]);
  const [selected, setSelected] = useState('');
  const [selectedMany, setSelectedMany] = useState([]);
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    if (companyId) hseApi.listCourses(companyId).then(setCourses);
  }, [companyId]);

  function toggleMany(id) {
    setSelectedMany(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }

  async function handleSubmit() {
    setSaving(true);
    try {
      if (mode === 'assign') {
        await hseApi.bulkAssign({ employeeIds, courseIds: selectedMany });
      } else {
        await hseApi.bulkPass({ employeeIds, courseId: selected });
      }
      onDone();
    } finally {
      setSaving(false);
    }
  }

  const isReady = mode === 'assign' ? selectedMany.length > 0 : !!selected;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: C.surfaceElevated, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '20px', width: '380px', maxHeight: '80vh', overflow: 'auto' }}>
        <h3 style={{ color: C.textPrimary, margin: '0 0 4px', fontSize: '15px' }}>
          {mode === 'assign' ? 'Assign Training' : 'Mark Group Pass'}
        </h3>
        <p style={{ color: C.textMuted, fontSize: '12px', marginBottom: '14px' }}>
          {mode === 'assign'
            ? `Assign course(s) to ${employeeIds.length} employee(s).`
            : `Mark ${employeeIds.length} employee(s) as passed for one course.`}
        </p>
        {courses.map(c => (
          <div
            key={c.id}
            onClick={() => mode === 'assign' ? toggleMany(c.id) : setSelected(c.id)}
            style={{
              padding: '10px', borderRadius: '7px', cursor: 'pointer', marginBottom: '6px',
              background: (mode === 'assign' ? selectedMany.includes(c.id) : selected === c.id) ? C.blue + '22' : C.surface,
              border: `1px solid ${(mode === 'assign' ? selectedMany.includes(c.id) : selected === c.id) ? C.blue + '44' : C.border}`,
            }}
          >
            <div style={{ color: C.textPrimary, fontWeight: 600, fontSize: '13px' }}>{c.name}</div>
            <div style={{ color: C.textMuted, fontSize: '11px' }}>{c.validityMonths ? `Valid ${c.validityMonths} months` : 'No expiry'}</div>
          </div>
        ))}
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '14px' }}>
          <button onClick={onClose} style={{ background: C.surface, border: `1px solid ${C.border}`, color: C.textSecondary, borderRadius: '6px', padding: '7px 14px', fontSize: '13px', cursor: 'pointer' }}>Cancel</button>
          <button onClick={handleSubmit} disabled={saving || !isReady} style={{ background: mode === 'assign' ? C.blue : C.green, color: '#fff', border: 'none', borderRadius: '6px', padding: '7px 14px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', opacity: saving || !isReady ? 0.5 : 1 }}>
            {saving ? 'Processing…' : mode === 'assign' ? 'Assign' : 'Mark Passed'}
          </button>
        </div>
      </div>
    </div>
  );
}
